import { FiberManualRecord } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Collapse,
  Divider,
  FormControl,
  Grid,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { setShowSuggestionModal } from "app/store/transmission/slice/transmissionSlice";
import ModalWrapper from "components/common/modals/modalWrapper/ModalWrapper";
import _, { isEqual } from "lodash";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { REPORT_SEVERITY, REPORT_SEVERITY_MAP } from "static/constants";
import { showErrorToast } from "utils/toast";
import "./ReportNoteSuggestionModal.css";
import SuggestionIcon from "./SuggestionIcon";
import IdentifiedAlert from "./IdentifiedAlert";

const ReportNoteSuggestionModal = ({ openModal, notes, handleReportNote }) => {
  const { reportNoteSuggestions, billableDate, saveWithProcess } = useSelector(
    (state) => state.transmission
  );

  const dispatch = useDispatch();
  const [suggestions, setSuggestions] = useState([...reportNoteSuggestions]);
  const [reportNotes, setReportNotes] = useState([...notes]);

  const [isDisabled, setIsDisabled] = useState(true);
  const [expandedSuggestions, setExpandedSuggestions] = useState({});

  useEffect(() => {
    const hasSuggestionChanged = !isEqual(suggestions, reportNoteSuggestions);

    setIsDisabled(!hasSuggestionChanged);
  }, [suggestions]);

  const updateNoteGroup = (
    group,
    setGroup,
    { note, description, alert, contentIndex }
  ) => {
    const newGroup = _.cloneDeep(group);
    const noteIndex = newGroup.findIndex((item) => item?.note === note);

    if (noteIndex === -1) return;

    const noteContent =
      newGroup[noteIndex]?.content?.length > 0
        ? newGroup[noteIndex]?.content
        : [
            {
              description: "",
              alert: "None",
              pageNumber: null,
            },
          ];

    if (alert) {
      const newSeverityIndex =
        (REPORT_SEVERITY.indexOf(alert) + 1) % REPORT_SEVERITY.length;
      noteContent[contentIndex].alert = REPORT_SEVERITY[newSeverityIndex];
    }

    if (description) {
      noteContent[contentIndex].description = description;
    }

    newGroup[noteIndex].content = noteContent;

    setGroup(newGroup);
  };

  const handleChange = (params) => {
    updateNoteGroup(reportNotes, setReportNotes, params);
    updateNoteGroup(suggestions, setSuggestions, params);
  };

  const toggleExpanded = (index) => {
    setExpandedSuggestions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleAISuggestion = async (isProceed = false) => {
    try {
      const reportNote = isProceed ? reportNotes : notes;

      const reportNoteProps = {
        notes: { reportNotes: reportNote },
        billingDate: billableDate,
        isProcessAction: saveWithProcess,
      };

      handleReportNote(reportNoteProps);

      dispatch(setShowSuggestionModal({ showModal: false }));
    } catch (error) {
      showErrorToast(error || error?.message);
    }
  };

  const handleClose = () => {
    setIsDisabled(true);
    dispatch(setShowSuggestionModal({ showModal: false, displayPopup: true }));
    setSuggestions(reportNoteSuggestions);
  };

  return (
    <ModalWrapper
      show={openModal}
      onClose={() => handleClose()}
      title={
        <div className="d-flex align-items-center">
          <div className="d-flex flex-column">
            <h3>Review Report Note Status</h3>
            <div className="modal-sub-title">
              Are you sure about the alert in notes? AI is suggesting the
              following alerts.
            </div>
          </div>
        </div>
      }
      actionButtons={[
        {
          label: "Continue without changes",
          variant: "outlined",
          color: "secondary",
          onClick: () => handleAISuggestion(false),
        },
        {
          label: "Continue with changes",
          disabled: isDisabled,
          onClick: () => handleAISuggestion(true),
        },
      ]}
      maxWidth="sm"
      showContentDivider
      footerMessage={
        <Alert severity="info" className="ai-info-alert">
          AI suggested the status above. Proceeding without changes will keep it
          as is.
        </Alert>
      }
    >
      <div className="modal-content">
        {suggestions?.map((suggestion, index) => (
          <List className="suggestion-list" key={index}>
            <ListItem disableGutters className="note-item">
              <ListItemText
                primary={suggestion?.note}
                primaryTypographyProps={{
                  className: "note-text",
                }}
              />

              <Grid container alignItems="center" className="identified-by-ai">
                <Grid
                  item
                  container
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Grid
                    item
                    container
                    flexDirection="row"
                    alignItems="center"
                    gap={0.5}
                    sm={8}
                  >
                    <SuggestionIcon />
                    <Typography className="identified-label">
                      Identified by AI:
                    </Typography>
                  </Grid>
                  {suggestion?.identifiedAlerts?.length > 1 && (
                    <Grid item sm={4}>
                      <Typography
                        className="identified-label"
                        textAlign="right"
                        onClick={() => toggleExpanded(index)}
                        sx={{ cursor: "pointer" }}
                      >
                        {expandedSuggestions[index]
                          ? `Show Less (${suggestion?.identifiedAlerts?.length})`
                          : `Show More (${suggestion?.identifiedAlerts?.length})`}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                {suggestion.identifiedAlerts?.length > 0 &&
                  (() => {
                    const sortedAlerts = [...suggestion.identifiedAlerts].sort(
                      (a, b) =>
                        (b.value === "Critical") - (a.value === "Critical")
                    );

                    return (
                      <>
                        <IdentifiedAlert
                          suggestion={suggestion}
                          alert={sortedAlerts[0]}
                          index={0}
                        />

                        <Collapse in={expandedSuggestions[index]}>
                          {sortedAlerts.slice(1).map((alert, i) => (
                            <IdentifiedAlert
                              key={i + 1}
                              suggestion={suggestion}
                              alert={alert}
                              index={i + 1}
                            />
                          ))}
                        </Collapse>
                      </>
                    );
                  })()}
              </Grid>
            </ListItem>

            <div className="suggestion-content-box">
              {suggestion.content?.map((content, contentIndex) => (
                <React.Fragment key={contentIndex}>
                  {contentIndex !== 0 && (
                    <Divider className="content-divider" />
                  )}

                  <div className="content-item">
                    <FormControl
                      fullWidth
                      variant="outlined"
                      margin="none"
                      size="small"
                    >
                      <Autocomplete
                        freeSolo
                        openOnFocus
                        clearOnBlur
                        value={content?.description || ""}
                        options={
                          suggestion?.placeholders?.filter(Boolean) || []
                        }
                        getOptionLabel={(option) => option}
                        onInputChange={(_event, newInputValue) => {
                          handleChange({
                            note: suggestion?.note,
                            description: newInputValue,
                            contentIndex,
                          });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            multiline
                            maxRows={3}
                            size="small"
                            variant="outlined"
                            sx={{
                              ".MuiOutlinedInput-root": {
                                border: "1px solid #e0e0e0",
                                borderRadius: "4px",
                              },
                              ".MuiAutocomplete-endAdornment": {
                                right: "2px !important",
                              },
                            }}
                          />
                        )}
                      />
                    </FormControl>
                    <Tooltip title={`Severity: ${content?.alert}`}>
                      <FiberManualRecord
                        onClick={() =>
                          handleChange({
                            note: suggestion?.note,
                            alert: content?.alert,
                            contentIndex,
                          })
                        }
                        style={{
                          color: REPORT_SEVERITY_MAP[content?.alert],
                        }}
                        className="severity-icon clickable"
                        value={content?.alert}
                      />
                    </Tooltip>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </List>
        ))}
      </div>
    </ModalWrapper>
  );
};

export default ReportNoteSuggestionModal;
